import { create } from 'zustand';
import * as auth from '@/services/auth/authService';
import type { Session, User } from '@/services/auth/authService';

type Status = 'authenticated' | 'unauthenticated';

interface AuthState {
  status: Status;
  user: User | null;
  /** True right after sign-up, until the welcome screen is dismissed. */
  isNewAccount: boolean;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signUp: (input: { name: string; email: string; password: string }) => Promise<void>;
  signOut: () => Promise<void>;
  dismissWelcome: () => void;
}

const fromSession = (session: Session | null) => ({
  status: (session ? 'authenticated' : 'unauthenticated') as Status,
  user: session?.user ?? null,
});

/**
 * Session state for the UI. Seeded synchronously from the auth service so an
 * authenticated user renders on the first frame (no redirect flash).
 */
export const useAuthStore = create<AuthState>((set) => ({
  ...fromSession(auth.getSession()),
  isNewAccount: false,
  signInWithPassword: async (email, password) => {
    set({ ...fromSession(await auth.signInWithPassword(email, password)), isNewAccount: false });
  },
  signInWithGoogle: async () => {
    set({ ...fromSession(await auth.signInWithGoogle()), isNewAccount: false });
  },
  signUp: async (input) => {
    set({ ...fromSession(await auth.signUp(input)), isNewAccount: true });
  },
  signOut: async () => {
    await auth.signOut();
    set({ status: 'unauthenticated', user: null, isNewAccount: false });
  },
  dismissWelcome: () => set({ isNewAccount: false }),
}));

// Keep tabs in sync: signing out in one tab signs out the others.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === 'latisent.session') useAuthStore.setState(fromSession(auth.getSession()));
  });
}
